import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import CheckIcon from '@mui/icons-material/Check';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import type { AccountData } from '../../api/models/AccountData';
import type { OrderData } from '../../api/models/OrderData';
import { ScheduleSession, ScheduleSessionStatus } from '../hooks/usePassScheduleData';
import { getTimeFormat } from '../../utils/getDateAndTimeFormat';
import { getStudioPrimary } from '../../shared/passDatedSessionPicker/utils';

type SessionCardProps = {
  session: ScheduleSession;
  account?: AccountData;
  guestOrder?: OrderData;
  selectable?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onToggle?: (session: ScheduleSession) => void;
  onCancel?: (session: ScheduleSession) => void;
  onBook?: (session: ScheduleSession) => void;
  onCancelWaitlist?: (session: ScheduleSession) => void;
};

const PLACEHOLDER_BACKGROUND = '#f5f5f5';

const statusChipLabelKeys: Record<ScheduleSessionStatus, string> = {
  [ScheduleSessionStatus.OPEN]: 'manage_passes.schedule.statusOpen',
  [ScheduleSessionStatus.CLOSED]: 'manage_passes.schedule.statusClosed',
  [ScheduleSessionStatus.WAITLIST]: 'manage_passes.schedule.statusWaitlist',
  [ScheduleSessionStatus.WAITLIST_REGISTERED]: 'manage_passes.schedule.statusWaitlistRegistered',
  [ScheduleSessionStatus.REGISTERED]: 'manage_passes.schedule.statusRegistered',
  [ScheduleSessionStatus.NOT_YET_ACTIVE]: 'manage_passes.schedule.statusNotYetActive',
  [ScheduleSessionStatus.PASS_EXPIRED]: 'manage_passes.schedule.statusPassExpired',
};

const getStatusChipSx = (
  status: ScheduleSessionStatus,
  theme: Theme,
  primary: { main: string; contrastText: string },
) => {
  switch (status) {
    case ScheduleSessionStatus.OPEN:
      return { color: primary.main, backgroundColor: alpha(primary.main, 0.125) };
    case ScheduleSessionStatus.REGISTERED:
      return { color: primary.contrastText, backgroundColor: primary.main };
    case ScheduleSessionStatus.WAITLIST:
    case ScheduleSessionStatus.WAITLIST_REGISTERED:
      return {
        color: theme.palette.warning.main,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.warning.main}`,
      };
    case ScheduleSessionStatus.PASS_EXPIRED:
      return {
        color: theme.palette.error.main,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.error.main}`,
      };
    default:
      return { color: theme.palette.text.secondary, backgroundColor: PLACEHOLDER_BACKGROUND };
  }
};

export const SessionCard = ({
  session,
  account,
  guestOrder,
  selectable,
  selected,
  disabled,
  onToggle,
  onCancel,
  onBook,
  onCancelWaitlist,
}: SessionCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const primary = getStudioPrimary(theme);
  const timeFormat = getTimeFormat(account);

  const hasGuest = !!session.registeredOrder?.guest_order_id;

  const formattedTime = moment.utc(session.timeSeconds * 1000).format(timeFormat);
  const durationHours = session.duration / 3600;

  const isBookable =
    !selectable && (session.status === ScheduleSessionStatus.OPEN || session.status === ScheduleSessionStatus.WAITLIST);

  const chipSx = selected
    ? { color: primary.contrastText, backgroundColor: primary.main }
    : getStatusChipSx(session.status, theme, primary);
  const chipLabel =
    selectable && selected ? t('pass_dated_sessions.statusSelected') : t(statusChipLabelKeys[session.status]);

  const card = (
    <Grid
      container
      onClick={isBookable ? () => onBook?.(session) : undefined}
      sx={{
        position: 'relative',
        backgroundColor: selected ? alpha(primary.main, 0.125) : 'background.paper',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        opacity: disabled ? 0.6 : 1,
        ...(isBookable && {
          cursor: 'pointer',
          WebkitTapHighlightColor: alpha(primary.main, 0.2),
          '&:active': { backgroundColor: alpha(primary.main, 0.08) },
        }),
        ...(selectable && {
          border: `3px solid ${selected ? primary.main : 'transparent'}`,
        }),
        ...(session.status === ScheduleSessionStatus.REGISTERED && {
          border: `2px solid ${primary.main}`,
        }),
        ...(session.status === ScheduleSessionStatus.WAITLIST_REGISTERED && {
          border: `2px solid ${theme.palette.warning.main}`,
        }),
      }}>
      <Chip
        label={chipLabel}
        size="small"
        icon={selectable && selected ? <CheckIcon /> : undefined}
        sx={{
          position: 'absolute',
          top: 8,
          insetInlineEnd: 10,
          fontWeight: 500,
          ...chipSx,
          '& .MuiChip-icon': { color: 'inherit' },
        }}
      />
      <Grid
        item
        sx={{
          width: 80,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 2,
          borderInlineEnd: '1px solid',
          borderColor: 'divider',
        }}>
        <Typography variant="h6" fontWeight={700}>
          {formattedTime}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {durationHours} {t('manage_passes.schedule.hours')}
        </Typography>
      </Grid>
      <Grid item xs sx={{ p: 2 }}>
        <Typography variant="body1" fontWeight={600} gutterBottom>
          {session.workshopName}
        </Typography>
        {session.totalTickets !== undefined && session.ticketsLeft !== undefined && (
          <Typography
            variant="caption"
            fontWeight={500}
            sx={{ display: 'block', mb: 1, color: session.ticketsLeft <= 3 ? 'error.main' : 'text.secondary' }}>
            {t('manage_passes.schedule.spotsLeft', {
              registered: session.totalTickets - session.ticketsLeft,
              total: session.totalTickets,
            })}
          </Typography>
        )}
        {session.employeeName && (
          <Grid container alignItems="center" gap={0.5}>
            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.disabled">
              {session.employeeName}
            </Typography>
          </Grid>
        )}
        {hasGuest && (
          <Grid container alignItems="center" gap={0.5}>
            <PeopleIcon sx={{ fontSize: 16, color: primary.main }} />
            <Typography variant="body2" fontWeight={500} sx={{ color: primary.main }}>
              {guestOrder
                ? t('manage_passes.schedule.guestBadge', { name: guestOrder.firstname })
                : t('manage_passes.schedule.guestInvited')}
            </Typography>
          </Grid>
        )}
        {session.status === ScheduleSessionStatus.NOT_YET_ACTIVE && session.passStartsOn && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('manage_passes.schedule.passStartsOn', {
              date: moment(session.passStartsOn).format('DD/MM/YYYY'),
            })}
          </Typography>
        )}
        {session.status === ScheduleSessionStatus.PASS_EXPIRED && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('manage_passes.schedule.passExpiredForSession')}
          </Typography>
        )}
        {session.status === ScheduleSessionStatus.REGISTERED && (
          <Grid container justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onCancel?.(session);
              }}
              sx={{ minHeight: 32 }}>
              {t('manage_passes.booking.cancel')}
            </Button>
          </Grid>
        )}
        {session.status === ScheduleSessionStatus.WAITLIST_REGISTERED && (
          <Grid container justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onCancelWaitlist?.(session);
              }}
              sx={{ minHeight: 32 }}>
              {t('manage_passes.schedule.leaveWaitlist')}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );

  if (!selectable) {
    return card;
  }

  return (
    <ButtonBase
      aria-pressed={!!selected}
      disabled={disabled}
      onClick={() => onToggle?.(session)}
      sx={{ display: 'block', width: '100%', textAlign: 'start', borderRadius: 2 }}>
      {card}
    </ButtonBase>
  );
};
